// RC-1 Reusable RCE Consumer Framework — Report hook.
//
// Single entry point every ReportShell-driven page uses to inherit data
// from the Engineering Context Engine. Reads its declarative config row
// from `configs/reportBindings.js`, asks the resolver for live values,
// and returns the four opt-in props ReportShell already accepts.
//
// Contract — the hook ALWAYS returns the same shape so callers never have
// to guard on an unknown report key:
//
//   {
//     initialState:    { ... } | null,
//     rceContextStrip: <node>  | null,
//     rceExtraAction:  <node>  | null,
//     binding:         <row>   | null,    // exposed for tests / diagnostics
//   }
//
// Note: `onAfterSave` is intentionally NOT provided in the POC. The
// snapshot-freeze action is a per-consumer concern (Kill Sheet freezes a
// kill sheet record; DMR freezes a fluid document). Generalising that
// across every report touches storage code and is V2-deferable. Header
// auto-fill — the immediate user-visible win — does not need it.

import { useMemo } from "react";
import { useEngineeringSession } from "./useEngineeringSession";
import { resolve } from "./resolver";
import { getReportBinding } from "./configs/reportBindings";
import RceContextStrip from "@/components/rce/RceContextStrip";

/** Convert an Engineering Session record into the ctx shape the resolver
 *  expects. Pure — exported for use in tests. */
export function sessionToResolverCtx(session, extra = {}) {
  return {
    documentId:    "",
    projectId:     session?.projectId     || "",
    rigId:         session?.rigId         || "",
    wellId:        session?.wellId        || "",
    bhaId:         session?.bhaId         || "",
    drillStringId: session?.drillStringId || "",
    mudProgramId:  session?.mudProgramId  || "",
    ...extra,
  };
}

/** Drop a value at a dotted path on an object, creating intermediate
 *  objects as needed. Returns the mutated target for chaining. Pure
 *  in-tree mutation — never recurses into prototypes. */
export function _assignDottedPath(target, dotted, value) {
  if (!target || !dotted) return target;
  const parts = dotted.split(".");
  let cur = target;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i];
    if (!cur[p] || typeof cur[p] !== "object") cur[p] = {};
    cur = cur[p];
  }
  cur[parts[parts.length - 1]] = value;
  return target;
}

/** Build the initial-state seed for a single binding row.
 *
 *  Skips paths the resolver cannot answer (`unknown` / `conflict`) and
 *  paths that resolve to blank values — so the user keeps seeing the
 *  empty input rather than a confusing "0" or "".
 *
 *  Returns `null` when nothing usable resolved, which causes ReportShell
 *  to fall through to its own emptyState() without an unnecessary merge.
 */
export function seedFromBinding(binding, ctx, resolveFn = resolve) {
  if (!binding || !binding.seed) return null;
  const seed = {};
  let any = false;
  for (const [rcePath, statePath] of Object.entries(binding.seed)) {
    const r = resolveFn(rcePath, ctx);
    if (!r) continue;
    if (r.confidence === "unknown" || r.confidence === "conflict") continue;
    if (r.value === "" || r.value === null || r.value === undefined) continue;
    _assignDottedPath(seed, statePath, String(r.value));
    any = true;
  }
  return any ? seed : null;
}

/**
 * Primary hook — call once per report page.
 *
 *   const rce = useReportRceWiring("cementingReport");
 *   return <ReportShell {...rce} ... />;
 *
 * Memoises on session identifiers so the report re-seeds when the active
 * Engineering Session changes (e.g. operator switches well in the session
 * switcher) — exactly mirroring how DMR does it today.
 */
export function useReportRceWiring(reportKey) {
  const { session } = useEngineeringSession();
  const binding = useMemo(() => getReportBinding(reportKey), [reportKey]);

  const ctx = useMemo(
    () => sessionToResolverCtx(session),
    [
      session?.projectId, session?.rigId, session?.wellId,
      session?.bhaId, session?.drillStringId, session?.mudProgramId,
    ]
  );

  const initialState = useMemo(() => {
    if (!session || !binding) return null;
    return seedFromBinding(binding, ctx);
  }, [binding, ctx, session]);

  const rceContextStrip = useMemo(() => {
    if (!session || !binding) return null;
    return (
      <RceContextStrip
        consumerKey={reportKey}
        consumerLabel={binding.label || reportKey}
        consumes={binding.consumes}
        ctx={ctx}
      />
    );
  }, [binding, ctx, reportKey, session]);

  // POC scope intentionally leaves `rceExtraAction` (the Synchronize button)
  // out — the on-mount seed is the immediate user-visible win. The button
  // ships in the framework's next iteration alongside the generic
  // snapshot-freeze action.

  return {
    initialState,
    rceContextStrip,
    rceExtraAction:  null,
    binding,
  };
}
